// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCsvExport } from '@hooks/other/useCsvExport';
import useExecutedFreezes from '@hooks/queries/registry/freeze/useExecutedFreezes';
import useExecutedUnfreezes from '@hooks/queries/registry/freeze/useExecutedUnfreezes';
import { useInstrumentConfigurationsById } from '@hooks/queries/useGetAllInstrumentConfigurations';
import Decimal from 'decimal.js';
import React, { useMemo } from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import {
  ExecutedFreeze,
  ExecutedUnfreeze,
} from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Model/MarkerEvents';
import CopyableText from '../../../components/CopyableText';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import TemplateContract from '../../../utils/TemplateContract';
import { fmt, formatTimestamp, parseTimestampToDate } from '../../../utils/common';
import { PageWrapper } from '../../PageWrapper';

type FreezeHistoryOperation = 'Freeze' | 'Unfreeze';

interface FreezeHistoryRow {
  contractId: string;
  operation: FreezeHistoryOperation;
  userId: string;
  registrar: string;
  instrumentId: string;
  holders: string[];
  totalAmount: string;
  freezeId?: string;
  reason?: string;
  createdAt: string;
}

const transformFreezeToRow = (contract: TemplateContract<ExecutedFreeze>): FreezeHistoryRow => {
  const { payload, contractId, createdAt } = contract;
  // NonEmpty has hd (head) and tl (tail), each element is a Tuple2 with _1 (party) and _2 (amount)
  const holders = [payload.holdersAmounts.hd._1, ...payload.holdersAmounts.tl.map((t) => t._1)];
  const totalAmount = [payload.holdersAmounts.hd._2, ...payload.holdersAmounts.tl.map((t) => t._2)]
    .reduce((sum, amt) => sum.plus(new Decimal(amt)), new Decimal(0))
    .toString();

  return {
    contractId,
    operation: 'Freeze',
    userId: payload.userId,
    registrar: payload.registrar,
    instrumentId: payload.instrumentId.id,
    holders,
    totalAmount,
    freezeId: payload.freezeId,
    reason: payload.reason,
    createdAt,
  };
};

const transformUnfreezeToRow = (contract: TemplateContract<ExecutedUnfreeze>): FreezeHistoryRow => {
  const { payload, contractId, createdAt } = contract;
  // NonEmpty has hd (head) and tl (tail), each element is a Tuple2 with _1 (party) and _2 (amount)
  const holders = [payload.holdersAmounts.hd._1, ...payload.holdersAmounts.tl.map((t) => t._1)];
  const totalAmount = [payload.holdersAmounts.hd._2, ...payload.holdersAmounts.tl.map((t) => t._2)]
    .reduce((sum, amt) => sum.plus(new Decimal(amt)), new Decimal(0))
    .toString();

  return {
    contractId,
    operation: 'Unfreeze',
    userId: payload.userId,
    registrar: payload.registrar,
    instrumentId: payload.instrumentId.id,
    holders,
    totalAmount,
    createdAt,
  };
};

interface FreezeHistoryProps {
  disableVirtualization?: boolean;
}

export const FreezeHistory: React.FC<FreezeHistoryProps> = ({ disableVirtualization }) => {
  const executedFreezes = useExecutedFreezes();
  const executedUnfreezes = useExecutedUnfreezes();
  const instrumentsById = useInstrumentConfigurationsById();

  const isLoading = executedFreezes.isLoading || executedUnfreezes.isLoading;

  const rows = useMemo(() => {
    const freezeRows = executedFreezes.data.map(transformFreezeToRow);
    const unfreezeRows = executedUnfreezes.data.map(transformUnfreezeToRow);

    return [...freezeRows, ...unfreezeRows].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [executedFreezes.data, executedUnfreezes.data]);

  const headers: GridColDef<FreezeHistoryRow>[] = useMemo(
    () => [
      {
        field: 'operation',
        headerName: 'Operation',
        renderCell: (params) => (
          <Chip
            label={params.row.operation}
            color={params.row.operation === 'Freeze' ? 'error' : 'success'}
            size="small"
            variant="outlined"
          />
        ),
        valueGetter: (params) => params.row.operation,
        flex: 0.5,
        minWidth: 100,
        filterable: true,
      },
      {
        field: 'holders',
        headerName: 'Wallet(s)',
        renderCell: (params) => {
          const { holders } = params.row;
          if (holders.length === 1) {
            return <PartyDisplay party={holders[0]} />;
          }
          return (
            <Tooltip title={holders.join(', ')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PartyDisplay party={holders[0]} />
                <Typography variant="caption" color="text.secondary">
                  +{holders.length - 1}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
        valueGetter: (params) => params.row.holders.join(', '),
        flex: 1.5,
        minWidth: 150,
        filterable: true,
      },
      {
        field: 'instrumentId',
        headerName: 'Instrument Id',
        valueGetter: (params) => params.row.instrumentId,
        renderCell: (params) => <CopyableText value={params.row.instrumentId} />,
        flex: 1,
        minWidth: 100,
        filterable: true,
      },
      {
        field: 'tokenName',
        headerName: 'Token Name',
        valueGetter: (params) => instrumentsById[params.row.instrumentId]?.payload.tokenName || '',
        flex: 1,
        minWidth: 100,
        filterable: true,
      },
      {
        field: 'tokenSymbol',
        headerName: 'Token Symbol',
        valueGetter: (params) =>
          instrumentsById[params.row.instrumentId]?.payload.tokenSymbol || '',
        flex: 1,
        minWidth: 100,
        filterable: true,
      },
      {
        field: 'cusip',
        headerName: 'CUSIP',
        valueGetter: (params) => instrumentsById[params.row.instrumentId]?.payload.cusip || '',
        flex: 1,
        minWidth: 100,
        filterable: true,
      },
      {
        field: 'totalAmount',
        headerName: 'Amount',
        renderCell: (params) => fmt(params.row.totalAmount, 2),
        valueGetter: (params) => params.row.totalAmount,
        sortComparator: (v1, v2) => new Decimal(v1 as number).cmp(new Decimal(v2 as number)),
        flex: 0.7,
        minWidth: 80,
        filterable: false,
      },
      {
        field: 'registrar',
        headerName: 'Registrar',
        renderCell: (params) => <PartyDisplay party={params.row.registrar} />,
        valueGetter: (params) => params.row.registrar,
        flex: 1.5,
        minWidth: 150,
        filterable: true,
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        type: 'dateTime',
        valueGetter: (params) => parseTimestampToDate(params.row.createdAt),
        renderCell: (params) => formatTimestamp(params.row.createdAt),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'userId',
        headerName: 'User ID',
        valueGetter: (params) => params.row.userId,
        flex: 1,
        minWidth: 100,
        filterable: true,
      },
      {
        field: 'freezeId',
        headerName: 'Freeze ID',
        valueGetter: (params) => params.row.freezeId || '-',
        flex: 1,
        minWidth: 100,
        filterable: true,
      },
      {
        field: 'reason',
        headerName: 'Reason',
        valueGetter: (params) => params.row.reason || '-',
        flex: 1,
        minWidth: 100,
        filterable: true,
      },
    ],
    [instrumentsById],
  );

  const { handleExport, isExportDisabled } = useCsvExport(headers, rows, {
    filename: 'freeze_history',
  });

  const buttons = [
    {
      name: 'Export CSV',
      onClick: handleExport,
      disabled: isExportDisabled,
    },
  ];

  if (isLoading) return <Loading />;

  return (
    <PageWrapper buttons={buttons} pageName="Freeze History">
      <DataTable
        columns={headers}
        rows={rows}
        rowKey={(row) => row.contractId}
        disableVirtualization={disableVirtualization}
      />
    </PageWrapper>
  );
};
